import { h, render } from "preact";

import Rheostat from "instantsearch.js/es/components/Slider/Rheostat";
import { connectRange } from "instantsearch.js/es/connectors";

const logarithmicScale = {
    toPosition(value, min, max) {
        return Math.log1p(value - min) / Math.log1p(max - min);
    },

    toValue(position, min, max) {
        return min + Math.expm1(position * Math.log1p(max - min));
    },
};

function classNames(...values) {
    return values.flat(Infinity).filter(Boolean).join(" ");
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function normalizeValue(value, precision, isLowerHandle) {
    const factor = 10 ** precision;

    return isLowerHandle ? Math.floor(value * factor) / factor : Math.ceil(value * factor) / factor;
}

export default function nonLinearRangeSlider(widgetParams) {
    const {
        container,
        attribute,
        min,
        max,
        precision = 0,
        steps = 1000,
        scale = logarithmicScale,
        tooltips = true,
        cssClasses = {},
    } = widgetParams || {};

    const containerNode =
        typeof container === "string" ? document.querySelector(container) : container;

    if (!containerNode) {
        throw new Error("The `container` option is required for nonLinearRangeSlider.");
    }

    if (!scale || typeof scale.toPosition !== "function" || typeof scale.toValue !== "function") {
        throw new Error("The `scale` option must define `toPosition` and `toValue` functions.");
    }

    if (!Number.isFinite(steps) || steps <= 0) {
        throw new Error("The `steps` option must be a positive finite number.");
    }

    if (!Number.isInteger(precision) || precision < 0) {
        throw new Error("The `precision` option must be a positive integer or zero.");
    }

    if (
        typeof tooltips === "object" &&
        tooltips !== null &&
        tooltips.format !== undefined &&
        typeof tooltips.format !== "function"
    ) {
        throw new Error("The `tooltips.format` option must be a function.");
    }

    const sliderSteps = Math.max(1, Math.round(steps));

    const renderSlider = ({ range, refine, start, canRefine }, isFirstRendering) => {
        if (isFirstRendering) {
            return;
        }

        const rangeMin = range.min;
        const rangeMax = range.max;
        const isDisabled = !canRefine;

        const toSliderValue = (value) => {
            if (isDisabled) {
                return 0;
            }

            return clamp(scale.toPosition(value, rangeMin, rangeMax), 0, 1) * sliderSteps;
        };

        const fromSliderValue = (value) => {
            if (isDisabled) {
                return rangeMin;
            }

            return clamp(
                scale.toValue(value / sliderSteps, rangeMin, rangeMax),
                rangeMin,
                rangeMax,
            );
        };

        const formatValue = (value) => {
            if (typeof tooltips === "object" && tooltips !== null && tooltips.format) {
                return tooltips.format(value);
            }

            return value.toLocaleString();
        };

        const createHandle = (props) => {
            const isLowerHandle = props["data-handle-key"] === 0;

            const value = normalizeValue(
                fromSliderValue(Number(props["aria-valuenow"])),
                precision,
                isLowerHandle,
            );

            const label = formatValue(value);

            const ariaValue = (value) => {
                if (value === undefined || value === null) {
                    return undefined;
                }

                return normalizeValue(fromSliderValue(Number(value)), precision, isLowerHandle);
            };

            return h(
                "button",
                {
                    ...props,
                    type: "button",
                    className: classNames(
                        props.className,
                        isLowerHandle ? "rheostat-handle-lower" : "rheostat-handle-upper",
                    ),
                    "aria-label": isLowerHandle ? "Minimum filter value" : "Maximum filter value",
                    "aria-valuemin": ariaValue(props["aria-valuemin"]),
                    "aria-valuemax": ariaValue(props["aria-valuemax"]),
                    "aria-valuenow": value,
                    "aria-valuetext": label,
                },

                tooltips &&
                    h(
                        "div",
                        {
                            className: "rheostat-tooltip",
                        },
                        label,
                    ),
            );
        };

        const startMin = start[0] === -Infinity ? rangeMin : start[0];

        const startMax = start[1] === Infinity ? rangeMax : start[1];

        const values = [
            toSliderValue(clamp(startMin, rangeMin, rangeMax)),
            toSliderValue(clamp(startMax, rangeMin, rangeMax)),
        ];

        render(
            h(
                "div",
                {
                    className: classNames(
                        "ais-RangeSlider",
                        cssClasses.root,
                        isDisabled && ["ais-RangeSlider--disabled", cssClasses.disabledRoot],
                    ),
                },

                h(Rheostat, {
                    disabled: isDisabled,
                    handle: createHandle,
                    min: 0,
                    max: isDisabled ? 0.001 : sliderSteps,
                    values,
                    onChange: ({ values: nextValues }) => {
                        const nextMin = fromSliderValue(nextValues[0]);

                        const nextMax = fromSliderValue(nextValues[1]);

                        refine([
                            normalizeValue(nextMin, precision, true),
                            normalizeValue(nextMax, precision, false),
                        ]);
                    },
                }),
            ),

            containerNode,
        );
    };

    const makeWidget = connectRange(renderSlider, () => render(null, containerNode));

    return {
        ...makeWidget({
            attribute,
            min,
            max,
            precision,
        }),

        $$type: "youdji.nonLinearRangeSlider",
        $$widgetType: "youdji.nonLinearRangeSlider",
    };
}
